import type { ExtensionType, Workspace } from '@blocksuite/affine/store';

import { CsvExtractor } from './csv-extractor';
import {
  cancelDatabaseImportJob,
  type DatabaseImportJob,
  type DatabaseImportJobStatusChange,
  type DatabaseImportSourcePayload,
  postDatabaseImportBatch,
  retryDatabaseImportJob,
  waitForDatabaseImportJobs,
} from './database-import-client';
import {
  disposeDatabaseImportWorkerRun,
  readNextDatabaseImportBatchInWorker,
  startCsvImportInWorker,
  startExcelImportInWorker,
} from './database-import-parser-worker-client';
import { importSourcePayload } from './database-import-payload';
import { ExcelExtractor } from './excel-extractor';
import {
  assertImportPreflightAllowsShardedMutation,
  preflightCsvFile,
  preflightExcelFile,
} from './import-preflight';
import {
  importShardedDatabasesLocalFirstFromBatches,
  type LocalShardedDatabaseImportProgressChange,
  throwIfLocalDatabaseImportAborted,
} from './local-sharded-importer';

export {
  cancelDatabaseImportJob,
  retryDatabaseImportJob,
  waitForDatabaseImportJobs,
};
export type { DatabaseImportJob, DatabaseImportJobStatusChange };

export type DatabaseImportJobResult = {
  docId: string;
  stats: {
    databaseCount: number;
    totalRows: number;
  };
  jobs: DatabaseImportJob[];
};

export type DatabaseImportLocalFirstResult = {
  databaseBlockIds: string[];
  docId: string;
  stats: {
    databaseCount: number;
    totalRows: number;
  };
  jobs: DatabaseImportJob[];
};

export type CsvDatabaseImportJobOptions = {
  workspaceId: string;
  docId: string;
  files: File[];
  hasHeader?: boolean;
  titleColumnIndex?: number;
  dropPosition?: { x: number; y: number };
  onJobsChanged?: DatabaseImportJobStatusChange;
};

export type CsvDatabaseImportLocalFirstOptions = Omit<
  CsvDatabaseImportJobOptions,
  'workspaceId' | 'onJobsChanged'
> & {
  collection: Workspace;
  extensions?: ExtensionType[];
  docName?: string;
  estimatedTotalRows?: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  signal?: AbortSignal;
};

export type ExcelDatabaseImportJobOptions = {
  workspaceId: string;
  docId: string;
  files: File[];
  selectedSheets?: Map<string, string[]>;
  hasHeader?: boolean;
  titleColumnIndex?: number;
  dropPosition?: { x: number; y: number };
  onJobsChanged?: DatabaseImportJobStatusChange;
};

export type ExcelDatabaseImportLocalFirstOptions = Omit<
  ExcelDatabaseImportJobOptions,
  'workspaceId' | 'onJobsChanged'
> & {
  collection: Workspace;
  extensions?: ExtensionType[];
  docName?: string;
  estimatedTotalRows?: number;
  onProgress?: LocalShardedDatabaseImportProgressChange;
  signal?: AbortSignal;
};

function selectedSheetsByFileName(
  selectedSheets?: Map<string, string[]>
): Record<string, string[]> | undefined {
  if (!selectedSheets || selectedSheets.size === 0) return undefined;
  return Object.fromEntries(selectedSheets.entries());
}

export async function importCsvFilesWithDatabaseImportJob(
  options: CsvDatabaseImportJobOptions
): Promise<DatabaseImportJobResult> {
  const extractor = new CsvExtractor();
  const sources: DatabaseImportSourcePayload[] = [];
  let totalRows = 0;

  for (const [index, file] of options.files.entries()) {
    const preflight = await preflightCsvFile({
      file,
      hasHeader: options.hasHeader,
    });
    assertImportPreflightAllowsShardedMutation([preflight]);

    const extraction = await extractor.extract({
      file,
      hasHeader: options.hasHeader,
    });
    totalRows += extraction.rowCount;
    sources.push(
      importSourcePayload(extraction, index, {
        titleColumnIndex: options.titleColumnIndex,
        dropPosition: options.dropPosition,
        sourceFileName: file.name,
      })
    );
  }

  const { jobs } = await postDatabaseImportBatch({
    workspaceId: options.workspaceId,
    docId: options.docId,
    sources,
  });
  options.onJobsChanged?.([...jobs]);

  const completedJobs = await waitForDatabaseImportJobs(
    options.workspaceId,
    jobs,
    options.onJobsChanged
  );

  return {
    docId: options.docId,
    stats: {
      databaseCount: sources.length,
      totalRows,
    },
    jobs: completedJobs,
  };
}

export async function importCsvFilesLocalFirst(
  options: CsvDatabaseImportLocalFirstOptions
): Promise<DatabaseImportLocalFirstResult> {
  let workerRunId: string | null = null;
  options.onProgress?.({
    phase: 'csv-reading',
    processedRows: 0,
    totalRows: options.estimatedTotalRows ?? 0,
    percent: null,
  });

  try {
    throwIfLocalDatabaseImportAborted(options.signal);
    const workerRun = await startCsvImportInWorker(
      {
        files: options.files,
        hasHeader: options.hasHeader,
        titleColumnIndex: options.titleColumnIndex,
        dropPosition: options.dropPosition,
      },
      options.signal
    );
    workerRunId = workerRun.runId;
    throwIfLocalDatabaseImportAborted(options.signal);

    const result = await importShardedDatabasesLocalFirstFromBatches({
      collection: options.collection,
      docId: options.docId,
      sources: workerRun.sources,
      extensions: options.extensions,
      docName: options.docName,
      onProgress: options.onProgress,
      signal: options.signal,
      readNextBatch: async () => {
        if (!workerRunId) return null;
        const batch = await readNextDatabaseImportBatchInWorker(
          workerRunId,
          options.signal
        );
        return batch.done ? null : batch.batch;
      },
    });

    return {
      databaseBlockIds: result.databaseBlockIds,
      docId: result.docId,
      stats: result.stats,
      jobs: [],
    };
  } finally {
    if (workerRunId) {
      await disposeDatabaseImportWorkerRun(workerRunId).catch(() => {});
    }
  }
}

export async function importExcelFilesWithDatabaseImportJob(
  options: ExcelDatabaseImportJobOptions
): Promise<DatabaseImportJobResult> {
  const extractor = new ExcelExtractor();
  const sources: DatabaseImportSourcePayload[] = [];
  let totalRows = 0;
  let databaseIndex = 0;

  for (const file of options.files) {
    const selectedSheets = options.selectedSheets?.get(file.name);
    const preflight = await preflightExcelFile({
      file,
      selectedSheets,
      hasHeader: options.hasHeader,
    });
    assertImportPreflightAllowsShardedMutation(preflight.sheets);

    const extraction = await extractor.extract({
      file,
      selectedSheets,
      hasHeader: options.hasHeader,
    });

    for (const sheet of extraction.sheets) {
      totalRows += sheet.rowCount;
      sources.push(
        importSourcePayload(sheet, databaseIndex, {
          titleColumnIndex: options.titleColumnIndex,
          dropPosition: options.dropPosition,
          sourceFileName: file.name,
          sourceSheetName: sheet.name,
        })
      );
      databaseIndex += 1;
    }
  }

  const { jobs } = await postDatabaseImportBatch({
    workspaceId: options.workspaceId,
    docId: options.docId,
    sources,
  });
  options.onJobsChanged?.([...jobs]);

  const completedJobs = await waitForDatabaseImportJobs(
    options.workspaceId,
    jobs,
    options.onJobsChanged
  );

  return {
    docId: options.docId,
    stats: {
      databaseCount: sources.length,
      totalRows,
    },
    jobs: completedJobs,
  };
}

export async function importExcelFilesLocalFirst(
  options: ExcelDatabaseImportLocalFirstOptions
): Promise<DatabaseImportLocalFirstResult> {
  let workerRunId: string | null = null;
  options.onProgress?.({
    phase: 'excel-reading',
    processedRows: 0,
    totalRows: options.estimatedTotalRows ?? 0,
    percent: null,
  });

  try {
    throwIfLocalDatabaseImportAborted(options.signal);
    const workerRun = await startExcelImportInWorker(
      {
        files: options.files,
        selectedSheetsByFileName: selectedSheetsByFileName(
          options.selectedSheets
        ),
        hasHeader: options.hasHeader,
        titleColumnIndex: options.titleColumnIndex,
        dropPosition: options.dropPosition,
      },
      options.signal
    );
    workerRunId = workerRun.runId;
    throwIfLocalDatabaseImportAborted(options.signal);

    const result = await importShardedDatabasesLocalFirstFromBatches({
      collection: options.collection,
      docId: options.docId,
      sources: workerRun.sources,
      extensions: options.extensions,
      docName: options.docName,
      onProgress: options.onProgress,
      signal: options.signal,
      readNextBatch: async () => {
        if (!workerRunId) return null;
        const batch = await readNextDatabaseImportBatchInWorker(
          workerRunId,
          options.signal
        );
        return batch.done ? null : batch.batch;
      },
    });

    return {
      databaseBlockIds: result.databaseBlockIds,
      docId: result.docId,
      stats: result.stats,
      jobs: [],
    };
  } finally {
    if (workerRunId) {
      await disposeDatabaseImportWorkerRun(workerRunId).catch(() => {});
    }
  }
}
